import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseClient } from "@/hooks/useSupabaseClient";
import type { MarketingSettings } from "@/types/sms";

const SETTINGS_QUERY_KEY = ["sms-settings"];

/**
 * Reads the signed-in store's marketing settings row. RLS scopes the select
 * to the caller's own store_id (sms_marketing_settings_select_own_store), so
 * a plain select().single() is enough — there is exactly one row per store.
 * sms_marketing_settings is not in the generated Database types yet, so this
 * goes through an untyped client (same pattern as usePosStores).
 */
export const useMarketingSettings = () => {
  const getSupabaseClient = useSupabaseClient();

  return useQuery<MarketingSettings>({
    queryKey: SETTINGS_QUERY_KEY,
    queryFn: async () => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const { data, error } = await sb.from("sms_marketing_settings").select("*").single();
      if (error) throw error;
      return data as MarketingSettings;
    },
    // This row carries the emergency kill switch, so it must never render
    // stale — always refetch on mount/focus rather than trusting a cached
    // copy that could be minutes old on another device.
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });
};

/** Updates the settings row and invalidates the settings query on success. */
export const useUpdateMarketingSettings = () => {
  const getSupabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: Partial<MarketingSettings> & { store_id: string }): Promise<MarketingSettings> => {
      const supabase = await getSupabaseClient();
      if (!supabase) throw new Error("Not authenticated");
      const sb = supabase as unknown as SupabaseClient;

      const { data, error } = await sb
        .from("sms_marketing_settings")
        .update(updates)
        .eq("store_id", updates.store_id)
        .select()
        .single();
      if (error) throw error;
      return data as MarketingSettings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SETTINGS_QUERY_KEY });
    },
  });
};
